import { useState, useMemo } from "react";
import { usePropertyAssetRegistry } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Skeleton } from "@/shared/ui/skeleton";
import { HierarchyView } from "@/features/assets/components/asset-registry/HierarchyView";
import { AssetListView } from "@/features/assets/components/asset-registry/AssetListView";
import { AssetDetailPanel } from "@/features/assets/components/asset-registry/AssetDetailPanel";
import { Search, List, Network, ChevronDown, ChevronUp, Building } from "lucide-react";
import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";

interface PropertyAssetRegistryProps {
  propertyId: string;
  /** Rendered at the end of the controls row (e.g. an Export button). */
  headerActions?: React.ReactNode;
}

function PropertyAssetRegistrySkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/40 bg-gradient-to-br from-card via-card to-card/80 shadow-lg">
        <CardHeader className="border-b border-border/50 bg-gradient-to-r from-background/50 via-background/30 to-transparent backdrop-blur-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-11 w-11 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>

            <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto lg:justify-end">
              <Skeleton className="h-8 w-24 rounded-lg" />
              <Skeleton className="h-9 w-36 rounded-lg" />
              <div className="hidden items-center gap-1.5 md:flex">
                <Skeleton className="h-8 w-24 rounded-md" />
                <Skeleton className="h-8 w-24 rounded-md" />
              </div>
              <Skeleton className="h-9 w-28 rounded-md" />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, categoryIndex) => (
              <div key={categoryIndex} className="space-y-2">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-8" />
                        <Skeleton className="h-2 w-16 rounded-full" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function PropertyAssetRegistry({
  propertyId,
  headerActions,
}: PropertyAssetRegistryProps) {
  const {
    categories,
    loading,
  } = usePropertyAssetRegistry(propertyId);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"hierarchy" | "list">("hierarchy");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showAssetModal, setShowAssetModal] = useState(false);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;
    return categories.map(category => ({
      ...category,
      assets: category.assets.filter(asset =>
        asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.model_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.assets.length > 0);
  }, [categories, searchTerm]);

  const expandAll = () => {
    setExpandedCategories(new Set(categories.map(c => c.id)));
  };
  const collapseAll = () => {
    setExpandedCategories(new Set());
  };
  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };
  const handleAssetClick = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowAssetModal(true);
  };
  if (loading) {
    return <PropertyAssetRegistrySkeleton />;
  }

  return <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input placeholder="Search assets by name, manufacturer, model number, model name, or serial..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      {/* Asset Hierarchy */}
      <Card className="overflow-hidden border-border/40 bg-gradient-to-br from-card via-card to-card/80 shadow-lg">
        <CardHeader className="border-b border-border/50 bg-gradient-to-r from-background/50 via-background/30 to-transparent backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Title Section */}
            <div className="flex items-center gap-3 group">
              <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20 group-hover:bg-primary/15 transition-all duration-300 group-hover:scale-105">
                <Building className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold tracking-tight">Assets</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Comprehensive asset management
                </p>
              </div>
            </div>

            {/* Controls Section */}
            <div className="flex flex-wrap items-center justify-end gap-3 w-full lg:w-auto lg:ml-auto">
              {/* Asset count badge */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border/50 backdrop-blur-sm">
                <Badge variant="outline" className="text-xs font-medium border-border/60 bg-background/50 hover:bg-background transition-colors">
                  {filteredCategories.reduce((sum, cat) => sum + cat.assets.length, 0)} <span className="text-muted-foreground ml-1">assets</span>
                </Badge>
              </div>

              {/* View Toggle */}
              <div className="inline-flex items-center rounded-lg border border-border/60 bg-background/50 p-1 backdrop-blur-sm shadow-sm">
                <Button variant={viewMode === "list" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("list")} className={`h-8 px-3 transition-all duration-200 ${viewMode === "list" ? "shadow-sm" : "hover:bg-muted/50"}`}>
                  <List className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline text-sm">List</span>
                </Button>
                <Button variant={viewMode === "hierarchy" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("hierarchy")} className={`h-8 px-3 transition-all duration-200 ${viewMode === "hierarchy" ? "shadow-sm" : "hover:bg-muted/50"}`}>
                  <Network className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline text-sm">Hierarchy</span>
                </Button>
              </div>

              {/* Expand/Collapse Actions */}
              <div className="hidden md:flex items-center gap-1.5">
                <Button variant="outline" size="sm" onClick={expandAll} className="h-8 px-3 text-xs border-border/60 hover:bg-muted/80 hover:border-border transition-all duration-200">
                  <ChevronDown className="h-3.5 w-3.5 mr-1.5" />
                  Expand
                </Button>
                <Button variant="outline" size="sm" onClick={collapseAll} className="h-8 px-3 text-xs border-border/60 hover:bg-muted/80 hover:border-border transition-all duration-200">
                  <ChevronUp className="h-3.5 w-3.5 mr-1.5" />
                  Collapse
                </Button>
              </div>

              {headerActions}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCategories.length > 0 ? viewMode === "hierarchy" ? <HierarchyView categories={filteredCategories} expandedCategories={expandedCategories} onToggleCategory={toggleCategory} onAssetClick={handleAssetClick} selectedAssetId={selectedAsset?.id} /> : <AssetListView categories={filteredCategories} onAssetClick={handleAssetClick} selectedAssetId={selectedAsset?.id} /> : <div className="text-center py-12 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">No assets found</p>
              <p className="text-sm">
                {searchTerm ? "Try adjusting your search criteria" : "Import assets to get started"}
              </p>
            </div>}
        </CardContent>
      </Card>

      {/* Asset Detail Modal */}
      <AssetDetailPanel asset={selectedAsset} isOpen={showAssetModal} onClose={() => setShowAssetModal(false)} />
    </div>;
}
